







































































import { Vue, Component, Watch } from 'vue-property-decorator'
import { DocumentFile } from '@/types/document'
import { ExStore } from 'vuex'
import { EventEmitter } from 'events'
import { DeleteConfirmResults } from '@/constants/utils'
import dayjs from 'dayjs'
import { StateChanger } from 'vue-infinite-loading'
import { isCordovaEnvironment } from '@/utils/cordova'

enum EventTypes {
  CONFIRM_DELETE
}

const numberFormatter = (n: number) => {
  //
  if (n >= 100) {
    return n.toFixed(0)
  }
  return n.toFixed(1)
}

const fileSizeFormatter = (
  size?: number,
  unit: 'B' | 'KB' | 'MB' | 'GB' | 'TB' = 'B'
): string => {
  // KBで入ってくる想定
  if (!size) {
    return '-'
  }
  if (size < 1024) {
    return `${numberFormatter(size)} ${unit}`
  }
  if (unit == 'B') return fileSizeFormatter(size / 1024, 'KB')
  if (unit == 'KB') return fileSizeFormatter(size / 1024, 'MB')
  if (unit == 'MB') return fileSizeFormatter(size / 1024, 'GB')
  if (unit == 'GB') return fileSizeFormatter(size / 1024, 'TB')
  return `${numberFormatter(size)} TB`
}

const extractNextPage = (url: string | undefined) => {
  if (!url) return null
  const pageMatch = url.match(/page=(\d+)/)
  return pageMatch ? parseInt(pageMatch[1]) : null
}

@Component({
  filters: {
    fileSize: fileSizeFormatter,
    formatDate(dateString: string) {
      return dayjs(dateString).format('M月D日 HH:mm')
    }
  },
  components: {
    DeleteConfirmModal: () =>
      import('@/components/common/modals/AlertModal.vue'),
    InfiniteLoading: () => import('vue-infinite-loading'),
    Loading: () => import('@/components/common/loadings/Index.vue')
  }
})
export default class DocumentIndex extends Vue {
  $store!: ExStore
  files = [] as DocumentFile[]
  nextPage: number | null = 1

  showDeleteConfirmModal = false
  events = new EventEmitter()

  get userInfo() {
    return this.$store.getters['user/userInfo']
  }

  get relativeInfo() {
    return this.$store.getters['user/relativeInfo']
  }

  get patientId(): number | null | undefined {
    return this.userInfo.isPatientRelative
      ? this.relativeInfo?.patient.id
      : this.userInfo.id
  }

  getSenderName(file: DocumentFile) {
    if (file.worker) {
      return `${file.worker.lastName}${file.worker.occupation.name}`
    }
    return `${this.userInfo.lastName}${this.userInfo.firstName}`
  }

  handleDeleteConfirmOk() {
    this.events.emit(EventTypes.CONFIRM_DELETE, DeleteConfirmResults.OK)
  }

  handleDeleteConfirmCancel() {
    this.events.emit(EventTypes.CONFIRM_DELETE, DeleteConfirmResults.CANCEL)
  }

  async handleDownload(documentId: number) {
    const patientId = this.patientId
    if (patientId == null) return
    const file = this.files.find(file => file.id === documentId)
    if (!file) throw new Error('does not exist file')
    if (isCordovaEnvironment()) {
      this.$postMessage.openFileDownload({
        isRelative: this.userInfo.isPatientRelative,
        isPatient: true,
        type: 'document',
        patientId,
        fileId: documentId,
        fileName: file.fileName || ''
      })
      return
    }
    const res = await this.$api.document.fetchFile({
      patientId,
      documentId
    })
    const url = window.URL.createObjectURL(res)
    const a = document.createElement('a')
    a.style.opacity = '0'
    document.body.appendChild(a)
    a.download = file.fileName || ''
    a.href = url
    a.click()
    a.remove()
    setTimeout(() => {
      URL.revokeObjectURL(url)
    }, 100)
  }

  async fetchFiles() {
    const patientId = this.patientId
    if (patientId == null) return
    const res = await this.$api.document.fetch({
      patientId,
      page: 1
    })
    this.nextPage = extractNextPage(res.next)
    this.files = res.results
  }

  handleFileUpload() {
    if (this.userInfo.isPatientRelative) {
      this.$store.dispatch('alert/showMessages', ['このボタンは無効です'])
      return
    }
    this.$router.push({ name: 'PatientDocumentUpload' })
  }

  handleNameChange(id: number) {
    if (this.userInfo.isPatientRelative) {
      this.$store.dispatch('alert/showMessages', ['このボタンは無効です'])
      return
    }
    this.$router.push({
      name: 'PatientDocumentEdit',
      params: { documentId: String(id) }
    })
  }

  async deleteFile(id: number) {
    if (this.userInfo.isPatientRelative) {
      this.$store.dispatch('alert/showMessages', ['このボタンは無効です'])
      return
    }
    const patientId = this.patientId
    if (patientId == null) return
    this.showDeleteConfirmModal = true
    const resust = await new Promise<DeleteConfirmResults>(resolve =>
      this.events.once(EventTypes.CONFIRM_DELETE, resolve)
    )
    this.showDeleteConfirmModal = false
    if (resust === DeleteConfirmResults.CANCEL) {
      return
    }

    await this.$api.document.delete({
      patientId,
      documentId: id
    })
    this.files = this.files.filter(file => file.id !== id)
  }

  created() {
    this.fetchFiles()
  }

  range(n: number) {
    return Array.from({ length: n }, (v, k) => k + 1)
  }

  async appendNextFiles() {
    const patientId = this.patientId
    if (patientId == null) return
    if (!this.nextPage) return
    const res = await this.$api.document.fetch({
      patientId,
      page: this.nextPage
    })
    this.nextPage = extractNextPage(res.next)
    this.files = [...this.files, ...res.results]
  }

  handleNextLoad($state: StateChanger) {
    this.appendNextFiles().then(() => {
      if (this.nextPage) {
        $state.loaded()
      } else {
        $state.complete()
      }
    })
  }
}
